import axiosInstance from './axiosInstance';
import config from './config';
import {
  CreateReferenceUploadBody,
  CreateReferenceUploadResponse,
  ReferenceResponse,
  ReferenceKind,
  ReferenceRowsResponse,
  ReferenceUpload,
  ReferenceUploadsResponse,
} from '../types/referenceData';

const referenceUrl = (deviceName: string): string =>
  `${config.fetchDailyApiUrl}/${encodeURIComponent(deviceName)}`;

const uploadsUrl = (deviceName: string): string =>
  `${referenceUrl(deviceName)}/reference/uploads`;

export async function getReferenceData(deviceName: string, startDate: string, endDate: string, signal?: AbortSignal): Promise<ReferenceResponse> {
  const { data } = await axiosInstance.get<ReferenceResponse>(referenceUrl(deviceName), {
    params: { mode: 'reference', start_date: startDate, end_date: endDate },
    signal,
  });
  return data;
}

export async function getReferenceRows(deviceName: string, kind: ReferenceKind, startDate: string, endDate: string, signal?: AbortSignal): Promise<ReferenceRowsResponse> {
  const { data } = await axiosInstance.get<ReferenceRowsResponse>(referenceUrl(deviceName), {
    params: { mode: 'reference_rows', kind, start_date: startDate, end_date: endDate },
    signal,
  });
  return data;
}

export async function listReferenceUploads(deviceName: string): Promise<ReferenceUploadsResponse> {
  const { data } = await axiosInstance.get<ReferenceUploadsResponse>(uploadsUrl(deviceName));
  return data;
}

export async function createReferenceUpload(deviceName: string, body: CreateReferenceUploadBody): Promise<CreateReferenceUploadResponse> {
  const { data } = await axiosInstance.post<CreateReferenceUploadResponse>(uploadsUrl(deviceName), body);
  return data;
}

export async function deleteReferenceUpload(deviceName: string, uploadId: string): Promise<ReferenceUpload> {
  const { data } = await axiosInstance.delete<{ upload: ReferenceUpload }>(`${uploadsUrl(deviceName)}/${encodeURIComponent(uploadId)}`);
  return data.upload;
}

export async function sendReferenceFile(post: CreateReferenceUploadResponse['post'], file: File): Promise<void> {
  const body = new FormData();
  for (const [name, value] of Object.entries(post.fields)) body.append(name, value);
  body.append('file', file);
  const response = await fetch(post.url, { method: 'POST', body });
  if (!response.ok) throw new Error(await response.text() || `Upload failed: ${response.status} ${response.statusText}`);
}

export async function sha256File(file: File): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}

export function referenceErrorText(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = error.response;
    if (typeof response === 'object' && response !== null && 'data' in response) {
      const data = response.data;
      if (typeof data === 'object' && data !== null && 'error' in data && typeof data.error === 'string') return data.error;
    }
  }
  return error instanceof Error ? error.message : 'Request failed';
}
